import React from "react";
import {
  Badge,
  Box,
  Flex,
  Heading,
  HStack,
  Stack,
  Text,
  VStack,
  Button,
} from "@chakra-ui/react";
import { Link, useLocation, useOutletContext } from "react-router-dom";
import { CheckIcon } from "@chakra-ui/icons";

const onboardingSteps = [
  {
    id: "welcome",
    label: "Welcome to Onboarding",
    path: "/onboarding",
  },
  {
    id: "jobber",
    label: "Connect Provider",
    path: "/onboarding/connect-provider",
  },
  {
    id: "custom-field",
    label: "Create Your Waitlist",
    path: "/onboarding/waitlist",
  },
  {
    id: "waitlist",
    label: "Customize Your Waitlist",
    path: "/onboarding/waitlist/customize",
  },
  {
    id: "availability",
    label: "Set Worker Availability",
    path: "/onboarding/availability",
  },
  {
    id: "turn-on",
    label: "Turn It On",
    path: "/onboarding/turn-on",
  },
];

const stepByPath = {
  "/onboarding": "welcome",
  "/onboarding/connect-provider": "jobber",
  "/onboarding/connect-jobber": "jobber",
  "/onboarding/waitlist": "custom-field",
  "/onboarding/waitlist/customize": "waitlist",
  "/onboarding/availability": "availability",
  "/onboarding/turn-on": "turn-on",
};

export default function OnboardingLayout({ children, title, subtitle, asideFooter }) {
  const location = useLocation();
  const outletContext = useOutletContext() || {};
  const initialConnectedProvider = outletContext.user?.connected_provider || null;
  const activeStepId = stepByPath[location.pathname] || "welcome";
  const [completionByStep, setCompletionByStep] = React.useState({ welcome: true });
  const [connectedProvider, setConnectedProvider] = React.useState(initialConnectedProvider);
  const visibleOnboardingSteps = connectedProvider === "service_m8"
    ? onboardingSteps.filter((step) => step.id !== "custom-field")
    : onboardingSteps;

  React.useEffect(() => {
    fetchProgress();
  }, [location.pathname]);

  React.useEffect(() => {
    const handleRefresh = () => fetchProgress();
    window.addEventListener("onboarding-progress-refresh", handleRefresh);
    return () => window.removeEventListener("onboarding-progress-refresh", handleRefresh);
  }, []);

  const fetchProgress = async () => {
    const next = { welcome: true };

    try {
      const response = await fetch("/api/onboarding_progress");
      const data = await response.json().catch(() => null);
      if (response.ok && data?.success) {
        (data.steps || []).forEach((step) => {
          next[step.id] = !!step.complete;
        });
      }
    } catch (error) {
      // Continue to fallback fetch below.
    }

    try {
      const response = await fetch("/api/current_user");
      const data = await response.json().catch(() => null);
      if (response.ok && data?.user) {
        next.jobber = !!data.user.provider_connected;
        setConnectedProvider(data.user.connected_provider || null);
      }
    } catch (error) {
      // Keep UI resilient - no-op if fallback endpoint fails.
    }

    try {
      const response = await fetch("/api/waitlist_custom_fields");
      const data = await response.json().catch(() => null);
      if (response.ok && data?.success) {
        const selected = data.selected || {};
        next["custom-field"] = data.not_applicable === true || !!(
          selected.custom_field_configuration_id ||
          selected.custom_field_id ||
          selected.custom_field_label
        );
      }
    } catch (error) {
      // Keep UI resilient - no-op if fallback endpoint fails.
    }

    try {
      if (window.sessionStorage?.getItem("onboarding.waitlist_viewed") === "1") {
        next.waitlist = true;
      }
    } catch (error) {
      // Keep UI resilient - no-op if storage is unavailable.
    }

    setCompletionByStep(next);
  };

  return (
    <Box maxW="1800px" mx="auto" px={{ base: 4, md: 6 }} py={{ base: 6, md: 8 }}>
      <Stack spacing={6}>
        {(title || subtitle) && (
          <Box>
            {title && <Heading size="lg">{title}</Heading>}
            {subtitle && (
              <Text color="gray.600" mt={1} fontSize="sm">
                {subtitle}
              </Text>
            )}
          </Box>
        )}

        <Flex gap={6} direction={{ base: "column", lg: "row" }} align="stretch">
          <Box
            w={{ base: "full", lg: "320px" }}
            bg="white"
            borderRadius="lg"
            boxShadow="sm"
            border="1px"
            borderColor="gray.200"
            p={5}
            position={{ lg: "sticky" }}
            top={{ lg: 6 }}
            alignSelf="flex-start"
          >
            <Text fontWeight="semibold" color="gray.900" mb={4}>
              Onboarding Steps
            </Text>
            <VStack spacing={3} align="stretch">
              {visibleOnboardingSteps.map((step, index) => {
                const isActive = step.id === activeStepId;
                const isComplete = completionByStep[step.id] === true;
                const content = (
                  <HStack
                    spacing={3}
                    p={3}
                    borderRadius="md"
                    border="1px"
                    borderColor={isComplete ? "green.300" : isActive ? "blue.200" : "gray.200"}
                    bg={isComplete ? "green.50" : isActive ? "blue.50" : "white"}
                    _hover={step.path ? { bg: isComplete ? "green.100" : isActive ? "blue.50" : "gray.50" } : undefined}
                    transition="all 0.15s"
                  >
                    {isComplete ? (
                      <Badge
                        fontSize="xs"
                        px={2.5}
                        py={0.5}
                        borderRadius="999px"
                        border="1px solid"
                        borderColor="green.300"
                        bg="green.100"
                        color="green.700"
                        fontWeight="700"
                      >
                        <CheckIcon boxSize={2.5} />
                      </Badge>
                    ) : (
                      <Badge
                        fontSize="xs"
                        px={2.5}
                        py={0.5}
                        borderRadius="999px"
                        border="1px solid"
                        borderColor={isActive ? "blue.300" : "gray.200"}
                        bg={isActive ? "blue.100" : "white"}
                        color={isActive ? "blue.700" : "gray.600"}
                        fontWeight="700"
                      >
                        {index + 1}
                      </Badge>
                    )}
                    <Text fontSize="sm" color={isComplete ? "green.800" : isActive ? "blue.800" : "gray.700"}>
                      {step.label}
                    </Text>
                  </HStack>
                );

                if (step.path) {
                  return (
                    <Button
                      key={step.id}
                      as={Link}
                      to={step.path}
                      variant="unstyled"
                      p={0}
                      height="auto"
                      textAlign="left"
                    >
                      {content}
                    </Button>
                  );
                }

                return (
                  <Box key={step.id} opacity={0.6} cursor="not-allowed">
                    {content}
                  </Box>
                );
              })}
            </VStack>
            {asideFooter && <Box mt={6}>{asideFooter}</Box>}
          </Box>

          <Box
            flex="1"
            bg="white"
            borderRadius="lg"
            boxShadow="sm"
            border="1px"
            borderColor="gray.200"
            p={{ base: 5, md: 6 }}
          >
            {children}
          </Box>
        </Flex>
      </Stack>
    </Box>
  );
}

export { onboardingSteps };
